import React, { useState, useEffect } from 'react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Calendar, CreditCard, IndianRupee, Filter } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#047857', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0'];

function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [dateFrom, dateTo]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo) params.append('end_date', dateTo);
      
      const response = await axios.get(`${API_URL}/api/analytics?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatShortCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value}`;
  };

  if (loading) {
    return (
      <div data-testid="analytics-loading" className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const monthlyData = analytics?.monthly_sales || [];
  const productData = analytics?.product_sales || [];
  const invoiceTypes = analytics?.invoice_types || { gst: 0, non_gst: 0 };

  const pieData = [
    { name: 'GST', value: invoiceTypes.gst },
    { name: 'Non-GST', value: invoiceTypes.non_gst }
  ];

  return (
    <div data-testid="analytics-page" className="space-y-6 animate-fade-in">
      {/* Date Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-zinc-500" />
            <span className="text-sm font-medium text-zinc-700">Filter by Date:</span>
          </div>
          <input
            data-testid="analytics-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
          />
          <span className="text-zinc-400">to</span>
          <input
            data-testid="analytics-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-sm text-emerald-700 hover:underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="analytics-today-card" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">Today's Sales</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.today_sales)}
              </p>
            </div>
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-emerald-700" />
            </div>
          </div>
        </div>

        <div data-testid="analytics-month-card" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">This Month</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.month_sales)}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-700" />
            </div>
          </div>
        </div>

        <div data-testid="analytics-year-card" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">Total Sales</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.year_sales)}
              </p>
              <p className="text-sm text-zinc-500 mt-1">{summary.total_invoices} invoices</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-purple-700" />
            </div>
          </div>
        </div>

        <div data-testid="analytics-emi-card" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">EMI Value</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.total_emi_value)}
              </p>
              <p className="text-sm text-zinc-500 mt-1">{summary.emi_invoices} EMI sales</p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-amber-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Sales Chart */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Monthly Sales Trend</h3>
          <div className="h-80">
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#047857" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#047857" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return months[parseInt(month) - 1] || value;
                    }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={formatShortCurrency}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Sales']}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#047857" 
                    fillOpacity={1} 
                    fill="url(#colorSales)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-400">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Product Sales Chart */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Top Products</h3>
          <div className="h-80">
            {productData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productData.slice(0, 7)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis 
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickFormatter={formatShortCurrency}
                  />
                  <YAxis 
                    type="category"
                    dataKey="product" 
                    tick={{ fontSize: 11 }}
                    width={100}
                    tickFormatter={(value) => value.length > 15 ? value.slice(0, 15) + '...' : value}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Sales']}
                  />
                  <Bar dataKey="sales" fill="#047857" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-400">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Types Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-zinc-200 p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Invoice Type Distribution</h3>
          <div className="h-64">
            {(pieData[0].value > 0 || pieData[1].value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-400">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-200 p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Sales Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-3xl font-bold text-emerald-700 font-[Manrope]">{summary.total_invoices || 0}</p>
              <p className="text-sm text-zinc-600 mt-1">Total Invoices</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-700 font-[Manrope]">{invoiceTypes.gst || 0}</p>
              <p className="text-sm text-zinc-600 mt-1">GST Invoices</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-700 font-[Manrope]">{invoiceTypes.non_gst || 0}</p>
              <p className="text-sm text-zinc-600 mt-1">Non-GST Invoices</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-700 font-[Manrope]">{summary.emi_invoices || 0}</p>
              <p className="text-sm text-zinc-600 mt-1">EMI Sales</p>
            </div>
          </div>

          {/* Average Values */}
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-zinc-500">Average Invoice Value</p>
                <p className="text-xl font-bold text-zinc-900 font-[Manrope]">
                  {summary.total_invoices > 0 
                    ? formatCurrency(summary.year_sales / summary.total_invoices) 
                    : formatCurrency(0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-zinc-500">Average EMI Value</p>
                <p className="text-xl font-bold text-zinc-900 font-[Manrope]">
                  {summary.emi_invoices > 0 
                    ? formatCurrency(summary.total_emi_value / summary.emi_invoices) 
                    : formatCurrency(0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
